import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import { buildClientLoginUrlWithNext, isClientPortalPath } from './clientLoginRedirect';

const isStaffRole = (role) =>
  ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPPORT', 'ROLE_CONTENT', 'ROLE_AUDITOR'].includes(role);

export const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const hasLoggedBlock = useRef(false);
  const pathname = location.pathname;
  const isClientPath = isClientPortalPath(pathname);
  const isAdminPath = pathname.startsWith('/admin');

  // Log admin route guard blocks (non-staff trying to access admin)
  useEffect(() => {
    if (!loading && user && requireAdmin && !isStaffRole(user.role) && !hasLoggedBlock.current) {
      hasLoggedBlock.current = true;
      apiClient.post('/auth/log-route-guard-block', {
        attempted_path: pathname
      }).catch(() => {});
    }
  }, [loading, user, requireAdmin, pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!user) {
    if (isClientPath) {
      const nextTarget = `${pathname}${location.search || ''}`;
      return <Navigate to={buildClientLoginUrlWithNext(nextTarget)} replace />;
    }
    return <Navigate to="/login" replace />;
  }

  // Staff (OWNER/ADMIN) on client path -> redirect to admin dashboard
  if (isClientPath && isStaffRole(user.role)) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Client role on admin path -> redirect to Today (priorities inbox)
  if (isAdminPath && !isStaffRole(user.role)) {
    return <Navigate to="/today" replace />;
  }

  // Admin route protection - allow only OWNER and ADMIN
  if (requireAdmin && !isStaffRole(user.role)) {
    return <Navigate to="/today" replace />;
  }

  // Tenant routing - redirect to tenant dashboard if not already there
  if (user.role === 'ROLE_TENANT' && !location.pathname.startsWith('/tenant')) {
    return <Navigate to="/tenant" replace />;
  }

  return children;
};
